import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation, Trans } from "react-i18next";
import {
  Search, Check, CalendarClock, Users, Sparkles, ArrowUpDown, Send,
  LayoutGrid, List as ListIcon, Table as TableIcon, SlidersHorizontal,
  MessageSquare, Mail, Trash2, Upload, FileSpreadsheet, AlertTriangle,
} from "lucide-react";
import api, { apiError, fileUrl } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { PageHeader, UserAvatar, EmptyState, Spinner } from "@/components/ui-bits";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { fmtMoney } from "@/lib/constants";
import { parseBulk, resolveBandJs, openSlots, countryOf, SORTS } from "./network/helpers";
import { NetAction, StatPill, RecoCard, ExpertCard, ExpertRow, ExpertTable, FilterSheet, ExpertSheet, RequestsView } from "./network/views";

export default function HrNetwork() {
  const { t } = useTranslation();
  const [experts, setExperts] = useState(null);
  const [network, setNetwork] = useState(new Set());
  const [requests, setRequests] = useState({ incoming: [], outgoing: [] });
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("rating");
  const [tab, setTab] = useState("discover");
  const [view, setView] = useState(() => localStorage.getItem("compeni_network_view") || "cards");
  const [filters, setFilters] = useState({ fields: [], minExp: 0, country: "all", languages: [] });
  const [filterOpen, setFilterOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [inviteTarget, setInviteTarget] = useState(null);
  const [inviteMsg, setInviteMsg] = useState("");
  const [inviteBusy, setInviteBusy] = useState(false);
  const [coachOpen, setCoachOpen] = useState(false);
  const [coachMode, setCoachMode] = useState("single");
  const [coachForm, setCoachForm] = useState({ name: "", email: "", message: "" });
  const [coachBusy, setCoachBusy] = useState(false);
  const [coachInvites, setCoachInvites] = useState([]);
  // bulk import
  const coachFileRef = useRef(null);
  const [importText, setImportText] = useState("");
  const [importMsg, setImportMsg] = useState("");
  const [importing, setImporting] = useState(false);
  const [importBusy, setImportBusy] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [bands, setBands] = useState([]);
  const [currency, setCurrency] = useState("EUR");
  const [needs, setNeeds] = useState([]);

  const setViewMode = (v) => { setView(v); localStorage.setItem("compeni_network_view", v); };

  const load = () => {
    api.get("/experts").then(({ data }) => setExperts(data)).catch(() => setExperts([]));
    api.get("/company/me").then(({ data }) => setNetwork(new Set(data.network || []))).catch(() => {});
    api.get("/company/requests").then(({ data }) => setRequests(data)).catch(() => {});
    api.get("/company/rate-settings").then(({ data }) => { setBands(data.bands || []); setCurrency(data.currency || "EUR"); }).catch(() => {});
    api.get("/company/needs").then(({ data }) => setNeeds(data || [])).catch(() => setNeeds([]));
  };
  useEffect(() => { load(); }, []);

  const outByExpert = useMemo(() => Object.fromEntries((requests.outgoing || []).map((r) => [r.expert_id, r])), [requests]);
  const inByExpert = useMemo(() => Object.fromEntries((requests.incoming || []).map((r) => [r.expert_id, r])), [requests]);
  const statusOf = (id) => network.has(id) ? "in_network" : outByExpert[id] ? "outgoing" : inByExpert[id] ? "incoming" : "none";

  const openInvite = (id) => {
    const e = (experts || []).find((x) => x.expert_id === id);
    if (!e) return;
    setSelected(null);
    setInviteMsg("");
    setInviteTarget(e);
  };
  const doInvite = async () => {
    if (!inviteTarget) return;
    setInviteBusy(true);
    try {
      const { data } = await api.post("/company/network/request", { expert_id: inviteTarget.expert_id, message: inviteMsg.trim() });
      toast.success(data.accepted ? t("hr.network.toastAdded") : t("hr.network.toastInviteSent"));
      setInviteTarget(null); setInviteMsg(""); load();
    } catch (err) { toast.error(apiError(err)); }
    finally { setInviteBusy(false); }
  };
  const removeFromNetwork = async (id) => {
    try { await api.delete(`/company/network/${id}`); toast.success(t("hr.network.toastRemoved")); setSelected(null); load(); }
    catch (err) { toast.error(apiError(err)); }
  };
  const accept = async (rid) => { try { await api.post(`/company/requests/${rid}/accept`); toast.success(t("hr.network.toastAdded")); load(); } catch (err) { toast.error(apiError(err)); } };
  const decline = async (rid) => { try { await api.post(`/company/requests/${rid}/decline`); toast.success(t("hr.network.toastDeclined")); load(); } catch (err) { toast.error(apiError(err)); } };
  const withdraw = async (rid) => { try { await api.delete(`/company/requests/${rid}`); toast.success(t("hr.network.toastWithdrawn")); load(); } catch (err) { toast.error(apiError(err)); } };

  const loadCoachInvites = () => api.get("/company/coach-invites").then(({ data }) => setCoachInvites(data)).catch(() => {});
  const openCoach = () => {
    setCoachForm({ name: "", email: "", message: "" });
    setCoachMode("single");
    setImportText(""); setImportMsg(""); setImportResult(null);
    setCoachOpen(true); loadCoachInvites();
  };
  const sendCoachInvite = async () => {
    if (!coachForm.email.trim()) return;
    setCoachBusy(true);
    try {
      const { data } = await api.post("/company/coach-invites", {
        name: coachForm.name.trim(), email: coachForm.email.trim(), message: coachForm.message.trim(),
      });
      toast.success(data.resent ? t("hr.network.coach.toastResent") : t("hr.network.coach.toastSent"));
      setCoachForm({ name: "", email: "", message: "" });
      loadCoachInvites();
    } catch (err) { toast.error(apiError(err)); }
    finally { setCoachBusy(false); }
  };
  const importCoachFile = async (fileEl) => {
    const file = fileEl?.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/company/coach-invites/parse-file", fd);
      if (!data.count) {
        toast.error(t("hr.network.coach.importNoEmails"));
      } else {
        const lines = data.contacts.map((c) => (c.name ? `${c.name}, ${c.email}` : c.email)).join("\n");
        setImportText((prev) => (prev.trim() ? prev.trim() + "\n" + lines : lines));
        toast.success(t("hr.network.coach.importDone", { count: data.count, file: file.name }));
      }
    } catch (err) { toast.error(apiError(err, t("hr.network.coach.importError"))); }
    finally { setImporting(false); if (fileEl) fileEl.value = ""; }
  };
  const sendCoachBulk = async () => {
    const rows = parseBulk(importText).filter((r) => r.valid);
    if (!rows.length) return;
    setImportBusy(true);
    try {
      const { data } = await api.post("/company/coach-invites/bulk", {
        invites: rows.map(({ name, email }) => ({ name, email })), message: importMsg.trim(),
      });
      setImportResult(data);
      toast.success(data.skipped_count
        ? t("hr.network.coach.bulkSkipped", { created: data.created_count, skipped: data.skipped_count })
        : t("hr.network.coach.bulkDone", { count: data.created_count }));
      loadCoachInvites();
    } catch (err) { toast.error(apiError(err)); }
    finally { setImportBusy(false); }
  };
  const revokeCoachInvite = async (id) => {
    try { await api.delete(`/company/coach-invites/${id}`); toast.success(t("hr.network.coach.toastRevoked")); loadCoachInvites(); }
    catch (err) { toast.error(apiError(err)); }
  };
  const coachStatusTag = (s) => {
    const map = { pending: ["statusPending", "text-warning bg-warning/10"], joined: ["statusJoined", "text-success bg-success/10"], revoked: ["statusRevoked", "text-muted-foreground bg-secondary"] };
    const [key, cls] = map[s] || map.pending;
    return <span className={`text-[10px] font-semibold uppercase tracking-wide rounded-full px-2 py-0.5 ${cls}`}>{t(`hr.network.coach.${key}`)}</span>;
  };

  const openDetail = async (e) => {
    if (!e?.expert_id) return;
    setSelected(e); setDetail(e); setDetailLoading(true);
    try { const { data } = await api.get(`/experts/${e.expert_id}`); setDetail(data); }
    catch (err) { console.error("Expert detail fetch failed:", err); } finally { setDetailLoading(false); }
  };

  const countries = useMemo(() => Array.from(new Set((experts || []).map((e) => countryOf(e.location)))).sort(), [experts]);
  const languages = useMemo(() => Array.from(new Set((experts || []).flatMap((e) => e.languages || []))).sort(), [experts]);
  const activeFilters = filters.fields.length + filters.languages.length + (filters.minExp ? 1 : 0) + (filters.country !== "all" ? 1 : 0);

  const displayed = useMemo(() => {
    if (!experts) return [];
    const s = q.toLowerCase();
    const base = experts.filter((e) => {
      const st = statusOf(e.expert_id);
      if (tab === "network") return st === "in_network";
      return st === "none" || st === "outgoing"; // discover shows not-in-network (invited stay visible with tag)
    });
    let list = base.filter((e) =>
      (filters.fields.length === 0 || (e.fields || []).some((f) => filters.fields.includes(f))) &&
      (!filters.minExp || (e.years_experience || 0) >= filters.minExp) &&
      (filters.country === "all" || countryOf(e.location) === filters.country) &&
      (filters.languages.length === 0 || (e.languages || []).some((l) => filters.languages.includes(l))) &&
      (!s || e.name.toLowerCase().includes(s) || (e.title || "").toLowerCase().includes(s) || (e.headline || "").toLowerCase().includes(s)));
    return [...list].sort(SORTS[sort]).map((e) => {
      const b = resolveBandJs(bands, e.years_experience);
      return b ? { ...e, corp_band: b.label, corp_rate_str: fmtMoney(b.day_rate, currency) } : e;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [experts, q, filters, sort, tab, network, requests, bands, currency]);

  const total = experts?.length || 0;
  const availableNow = (experts || []).filter((e) => openSlots(e) > 0).length;
  const reqCount = (requests.incoming?.length || 0) + (requests.outgoing?.length || 0);

  const topFields = useMemo(() => {
    const counts = {};
    (needs || []).forEach((n) => { if (n.field) counts[n.field] = (counts[n.field] || 0) + 1; });
    return Object.entries(counts).sort((a, b) => b[1] - a[1]).map(([f]) => f);
  }, [needs]);
  const basedOnGoals = topFields.length > 0;

  const recommendations = useMemo(() => {
    if (!experts) return [];
    const rankPos = Object.fromEntries(topFields.map((f, i) => [f, i]));
    const candidates = experts.filter((e) => statusOf(e.expert_id) === "none");
    const scored = candidates.map((e) => {
      const matched = (e.fields || []).filter((f) => f in rankPos);
      const bestPos = matched.length ? Math.min(...matched.map((f) => rankPos[f])) : 999;
      return { e, matched, bestPos };
    });
    scored.sort((a, b) =>
      a.bestPos - b.bestPos ||
      b.matched.length - a.matched.length ||
      (b.e.rating_avg || 0) - (a.e.rating_avg || 0) ||
      (b.e.years_experience || 0) - (a.e.years_experience || 0));
    return scored.slice(0, 4).map(({ e, matched }) => {
      const b = resolveBandJs(bands, e.years_experience);
      const topMatched = topFields.find((f) => matched.includes(f)) || null;
      return { ...e, corp_band: b?.label, corp_rate_str: b ? fmtMoney(b.day_rate, currency) : undefined, reco_reason: topMatched };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [experts, network, requests, topFields, bands, currency]);

  const renderAction = (e, status) => (
    <NetAction status={status} id={e.expert_id} onInvite={openInvite} onRemove={removeFromNetwork} onGoRequests={() => setTab("requests")} />
  );

  const VIEWS = [
    { id: "cards", icon: LayoutGrid, label: t("hr.network.viewCards") },
    { id: "list", icon: ListIcon, label: t("hr.network.viewList") },
    { id: "table", icon: TableIcon, label: t("hr.network.viewTable") },
  ];

  const importRows = parseBulk(importText);
  const importValid = importRows.filter((r) => r.valid);
  const importInvalid = importRows.length - importValid.length;

  return (
    <AppShell>
      <PageHeader eyebrow={t("hr.eyebrow")} title={t("hr.network.title")} description={t("hr.network.subtitle")}>
        <Button data-testid="invite-coach-btn" variant="outline" onClick={openCoach} className="rounded-lg gap-1.5 bg-card">
          <Mail size={15} /> {t("hr.network.coach.btn")}
        </Button>
      </PageHeader>

      <div className="flex flex-wrap gap-2 mb-5">
        <StatPill icon={Users}>{t("hr.network.statInNetwork", { count: network.size })}</StatPill>
        <StatPill icon={Sparkles}>{t("hr.network.statVerified", { count: total })}</StatPill>
        <StatPill icon={CalendarClock}>{t("hr.network.statAvailable", { count: availableNow })}</StatPill>
        {reqCount > 0 && <StatPill icon={Send}>{t("hr.network.statRequests", { count: reqCount })}</StatPill>}
      </div>

      <Tabs value={tab} onValueChange={setTab} className="mb-5">
        <TabsList className="rounded-lg">
          <TabsTrigger value="discover" data-testid="tab-discover" className="rounded-md gap-1.5"><Sparkles size={14} /> {t("hr.network.discover")}</TabsTrigger>
          <TabsTrigger value="requests" data-testid="tab-requests" className="rounded-md gap-1.5">
            <Send size={14} /> {t("hr.network.requests")} {reqCount > 0 && <span className="min-w-5 h-5 px-1 grid place-items-center rounded-full bg-warning/15 text-warning text-[11px] font-semibold">{reqCount}</span>}
          </TabsTrigger>
          <TabsTrigger value="network" data-testid="tab-network" className="rounded-md gap-1.5">
            <Users size={14} /> {t("hr.network.myNetwork")} {network.size > 0 && <span className="min-w-5 h-5 px-1 grid place-items-center rounded-full bg-primary/10 text-primary text-[11px] font-semibold">{network.size}</span>}
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {tab === "requests" ? (
        <RequestsView requests={requests} onAccept={accept} onDecline={decline} onWithdraw={withdraw} onView={openDetail} onBrowse={() => setTab("discover")} />
      ) : (
        <>
          {tab === "discover" && !q && activeFilters === 0 && recommendations.length > 0 && (
            <div data-testid="reco-strip" className="mb-6 rounded-[0.9rem] border border-primary/20 bg-primary/[0.03] p-4 animate-fade-up">
              <div className="flex items-center gap-2.5 mb-3.5">
                <span className="grid place-items-center w-8 h-8 rounded-lg bg-primary/10 text-primary shrink-0"><Sparkles size={16} /></span>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-foreground">{t("hr.network.reco.title")}</p>
                  <p className="text-xs text-muted-foreground">{basedOnGoals ? t("hr.network.reco.subtitleGoals") : t("hr.network.reco.subtitleTop")}</p>
                </div>
              </div>
              <div className="flex gap-3 overflow-x-auto pb-1 snap-x">
                {recommendations.map((e) => (
                  <RecoCard key={e.expert_id} e={e} reason={e.reco_reason} onView={openDetail} action={renderAction(e, statusOf(e.expert_id))} />
                ))}
              </div>
            </div>
          )}
          {/* Controls */}
          <div className="flex flex-col sm:flex-row gap-3 mb-6">
            <div className="relative flex-1">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input data-testid="network-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("hr.network.searchPlaceholder")} className="h-11 rounded-lg pl-9" />
            </div>
            <Button variant="outline" onClick={() => setFilterOpen(true)} data-testid="filter-btn" className="h-11 rounded-lg gap-1.5 bg-card">
              <SlidersHorizontal size={15} /> {t("common.filters")}
              {activeFilters > 0 && <span className="min-w-5 h-5 px-1 grid place-items-center rounded-full bg-primary text-primary-foreground text-[11px] font-semibold">{activeFilters}</span>}
            </Button>
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger data-testid="sort-select" className="h-11 rounded-lg sm:w-48 gap-1.5"><ArrowUpDown size={14} className="text-muted-foreground" /><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="rating">{t("hr.network.sortRating")}</SelectItem>
                <SelectItem value="experience">{t("hr.network.sortExperience")}</SelectItem>
                <SelectItem value="availability">{t("hr.network.sortAvailability")}</SelectItem>
                <SelectItem value="name">{t("hr.network.sortName")}</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex items-center rounded-lg border border-border bg-card p-0.5 h-11" data-testid="view-toggle">
              {VIEWS.map((v) => (
                <button key={v.id} data-testid={`view-${v.id}`} onClick={() => setViewMode(v.id)} title={v.label}
                  className={`grid place-items-center w-9 h-full rounded-md transition-colors ${view === v.id ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
                  <v.icon size={16} />
                </button>
              ))}
            </div>
          </div>

          {experts === null ? (
            <div className="grid place-items-center py-24"><Spinner className="text-primary w-7 h-7" /></div>
          ) : displayed.length === 0 ? (
            tab === "network" ? (
              <EmptyState icon={Users} title={t("hr.network.networkEmpty")} description={t("hr.network.networkEmptyDesc")}
                action={<Button className="rounded-lg gap-1.5" onClick={() => setTab("discover")}><Sparkles size={15} /> {t("hr.network.browseExperts")}</Button>} />
            ) : (
              <EmptyState icon={Search} title={t("hr.network.noResults")} description={t("hr.network.noResultsDesc")} />
            )
          ) : (
            <>
              <p className="text-xs text-muted-foreground mb-3">{t("hr.network.count", { count: displayed.length })}</p>
              {view === "cards" && (
                <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-5">
                  {displayed.map((e, i) => (
                    <ExpertCard key={e.expert_id} e={e} i={i} status={statusOf(e.expert_id)} onView={openDetail} actions={renderAction(e, statusOf(e.expert_id))} />
                  ))}
                </div>
              )}
              {view === "list" && (
                <div className="space-y-3">
                  {displayed.map((e) => (
                    <ExpertRow key={e.expert_id} e={e} status={statusOf(e.expert_id)} onView={openDetail} actions={renderAction(e, statusOf(e.expert_id))} />
                  ))}
                </div>
              )}
              {view === "table" && (
                <ExpertTable list={displayed} statusOf={statusOf} renderAction={renderAction} onView={openDetail} />
              )}
            </>
          )}
        </>
      )}

      <FilterSheet open={filterOpen} onOpenChange={setFilterOpen} filters={filters} setFilters={setFilters} countries={countries} languages={languages} activeCount={displayed.length} />

      <ExpertSheet
        open={!!selected}
        onOpenChange={(o) => !o && setSelected(null)}
        expert={detail}
        loading={detailLoading}
        status={detail ? statusOf(detail.expert_id) : "none"}
        onInvite={openInvite}
        onRemove={removeFromNetwork}
      />

      <Dialog open={!!inviteTarget} onOpenChange={(o) => !o && setInviteTarget(null)}>
        <DialogContent className="rounded-[0.8rem]" data-testid="invite-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><MessageSquare size={18} className="text-primary" /> {t("hr.network.inviteTitle", { name: inviteTarget?.name })}</DialogTitle>
            <DialogDescription>{t("hr.network.inviteDesc")}</DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-3 rounded-xl border border-border p-3">
            <UserAvatar src={fileUrl(inviteTarget?.picture)} name={inviteTarget?.name} size={40} />
            <div className="min-w-0">
              <p className="text-sm font-medium truncate">{inviteTarget?.name}</p>
              <p className="text-xs text-muted-foreground truncate">{inviteTarget?.title}</p>
            </div>
          </div>
          <Textarea data-testid="invite-message" value={inviteMsg} onChange={(e) => setInviteMsg(e.target.value)} maxLength={500}
            placeholder={t("hr.network.invitePlaceholder", { name: inviteTarget?.name?.split(" ")[0] || "there", field: (inviteTarget?.fields || [])[0] || "leadership" })}
            className="rounded-lg min-h-[110px]" />
          <div className="text-[11px] text-muted-foreground text-right -mt-1">{inviteMsg.length}/500</div>
          <DialogFooter>
            <Button variant="outline" className="rounded-lg" onClick={() => setInviteTarget(null)} data-testid="invite-cancel">{t("common.cancel")}</Button>
            <Button className="rounded-lg gap-1.5" onClick={doInvite} disabled={inviteBusy} data-testid="invite-send">{inviteBusy ? <Spinner /> : <><Send size={15} /> {t("common.sendInvitation")}</>}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={coachOpen} onOpenChange={setCoachOpen}>
        <DialogContent className="rounded-[0.8rem] max-w-lg" data-testid="coach-invite-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Mail size={18} className="text-primary" /> {t("hr.network.coach.dialogTitle")}</DialogTitle>
            <DialogDescription>{t("hr.network.coach.dialogDesc")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Tabs value={coachMode} onValueChange={setCoachMode} className="w-full">
              <TabsList className="rounded-lg grid grid-cols-2 w-full">
                <TabsTrigger value="single" data-testid="coach-tab-single" className="rounded-md gap-1.5"><Mail size={14} /> {t("hr.network.coach.tabSingle")}</TabsTrigger>
                <TabsTrigger value="import" data-testid="coach-tab-import" className="rounded-md gap-1.5"><Upload size={14} /> {t("hr.network.coach.tabImport")}</TabsTrigger>
              </TabsList>
            </Tabs>

            {coachMode === "single" ? (
              <>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-sm">{t("hr.network.coach.nameLabel")}</Label>
                    <Input data-testid="coach-name" value={coachForm.name} onChange={(e) => setCoachForm({ ...coachForm, name: e.target.value })} placeholder={t("hr.network.coach.namePlaceholder")} className="h-11 rounded-lg" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-sm">{t("hr.network.coach.emailLabel")}</Label>
                    <Input data-testid="coach-email" type="email" value={coachForm.email} onChange={(e) => setCoachForm({ ...coachForm, email: e.target.value })} placeholder={t("hr.network.coach.emailPlaceholder")} className="h-11 rounded-lg" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm">{t("hr.network.coach.messageLabel")}</Label>
                  <Textarea data-testid="coach-message" value={coachForm.message} onChange={(e) => setCoachForm({ ...coachForm, message: e.target.value })} maxLength={500} placeholder={t("hr.network.coach.messagePlaceholder")} className="rounded-lg min-h-[90px]" />
                </div>
                <Button data-testid="coach-invite-send" onClick={sendCoachInvite} disabled={coachBusy || !coachForm.email.trim()} className="w-full rounded-lg gap-1.5">
                  {coachBusy ? <Spinner /> : <><Send size={15} /> {t("hr.network.coach.send")}</>}
                </Button>
              </>
            ) : importResult ? (
              <div className="space-y-4" data-testid="coach-import-result">
                {importResult.created_count > 0 && (
                  <div className="rounded-lg border border-success/30 bg-success/5 p-4">
                    <p className="text-sm font-medium text-success mb-2 inline-flex items-center gap-1.5"><Check size={15} /> {t("hr.network.coach.invitedCount", { count: importResult.created_count })}</p>
                    <div className="space-y-1.5 max-h-52 overflow-y-auto">
                      {importResult.created.map((c) => (
                        <div key={c.email} className="flex items-center justify-between gap-3 bg-card border border-border rounded-md px-3 py-2 text-sm">
                          <span className="min-w-0 truncate"><span className="font-medium">{c.name}</span> <span className="text-muted-foreground">· {c.email}</span></span>
                          {c.resent && <span className="text-[10px] uppercase tracking-wide text-warning shrink-0">{t("hr.network.coach.resentTag")}</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {importResult.skipped_count > 0 && (
                  <div className="rounded-lg border border-warning/30 bg-warning/5 p-4">
                    <p className="text-sm font-medium text-warning mb-2 inline-flex items-center gap-1.5"><AlertTriangle size={15} /> {t("hr.network.coach.skippedCount", { count: importResult.skipped_count })}</p>
                    <div className="space-y-1 text-xs text-muted-foreground max-h-32 overflow-y-auto">
                      {importResult.skipped.map((s, i) => (<div key={`${s.email}-${i}`} className="flex justify-between gap-3"><span className="truncate">{s.email}</span><span className="shrink-0">{s.reason}</span></div>))}
                    </div>
                  </div>
                )}
                <Button variant="outline" className="w-full rounded-lg" data-testid="coach-import-more" onClick={() => { setImportResult(null); setImportText(""); setImportMsg(""); }}>{t("hr.network.coach.importMore")}</Button>
              </div>
            ) : (
              <div className="space-y-3">
                <input ref={coachFileRef} type="file" accept=".xlsx,.xlsm,.csv,.docx,.pdf,.txt" hidden onChange={(e) => importCoachFile(e.target)} data-testid="coach-import-input" />
                <button type="button" data-testid="coach-import-btn" onClick={() => coachFileRef.current?.click()} disabled={importing}
                  className="w-full rounded-lg border border-dashed border-border bg-secondary/40 hover:border-primary/40 hover:bg-secondary/60 transition-colors p-4 flex items-center gap-3 text-left disabled:opacity-60">
                  <span className="grid place-items-center w-9 h-9 rounded-lg bg-primary/10 text-primary shrink-0">{importing ? <Spinner /> : <Upload size={17} strokeWidth={1.75} />}</span>
                  <span className="min-w-0">
                    <span className="block text-sm font-medium text-foreground">{importing ? t("hr.network.coach.reading") : t("hr.network.coach.importFile")}</span>
                    <span className="block text-xs text-muted-foreground">{t("hr.network.coach.importHint")}</span>
                  </span>
                  <FileSpreadsheet size={18} className="text-muted-foreground ml-auto shrink-0" />
                </button>
                <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.08em] text-muted-foreground">
                  <span className="flex-1 h-px bg-border" /> {t("hr.network.coach.orPaste")} <span className="flex-1 h-px bg-border" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm">{t("hr.network.coach.listLabel")}</Label>
                  <Textarea data-testid="coach-import-textarea" value={importText} onChange={(e) => setImportText(e.target.value)}
                    placeholder={"Dr. Sarah Lin, sarah@coachpool.io\nMarcus Webb, marcus@mentors.co\nalex@expert.com"} className="rounded-lg min-h-36 font-mono text-sm" />
                  <p className="text-xs text-muted-foreground"><Trans i18nKey="hr.network.coach.formatHint" components={{ b: <span className="font-medium text-foreground" /> }} /></p>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm">{t("hr.network.coach.sharedMessage")}</Label>
                  <Textarea data-testid="coach-import-message" value={importMsg} onChange={(e) => setImportMsg(e.target.value)} maxLength={500} placeholder={t("hr.network.coach.sharedMessagePlaceholder")} className="rounded-lg min-h-[70px]" />
                </div>
                {importRows.length > 0 && (
                  <div className="flex items-center gap-3 text-xs" data-testid="coach-import-preview">
                    <span className="inline-flex items-center gap-1 text-success"><Check size={13} /> {t("hr.network.coach.ready", { count: importValid.length })}</span>
                    {importInvalid > 0 && <span className="inline-flex items-center gap-1 text-warning"><AlertTriangle size={13} /> {t("hr.network.coach.invalidIgnored", { count: importInvalid })}</span>}
                  </div>
                )}
                <Button data-testid="coach-import-send" onClick={sendCoachBulk} disabled={importBusy || !importValid.length} className="w-full rounded-lg gap-1.5">
                  {importBusy ? <Spinner /> : <><Send size={15} /> {t("hr.network.coach.inviteCount", { count: importValid.length })}</>}
                </Button>
              </div>
            )}
          </div>

          {coachInvites.length > 0 && (
            <div className="pt-4 mt-2 border-t border-border" data-testid="coach-invite-list">
              <p className="text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground mb-2">{t("hr.network.coach.sentTitle")}</p>
              <div className="space-y-2 max-h-56 overflow-y-auto">
                {coachInvites.map((ci) => (
                  <div key={ci.invite_id} data-testid={`coach-invite-${ci.invite_id}`} className="flex items-center gap-3 rounded-lg border border-border p-2.5">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-foreground truncate">{ci.name || ci.email}</p>
                      {ci.name && <p className="text-xs text-muted-foreground truncate">{ci.email}</p>}
                    </div>
                    {coachStatusTag(ci.status)}
                    {ci.status === "pending" && (
                      <button data-testid={`coach-revoke-${ci.invite_id}`} onClick={() => revokeCoachInvite(ci.invite_id)} title={t("hr.network.coach.revoke")}
                        className="grid place-items-center w-7 h-7 rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/5 transition-colors shrink-0">
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
